import React from 'react';
import axios from 'axios';
import { DEFAULT_ROOMS } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #990 — Admin: create/edit a custom NPC / Monster / Pokemon / Pokemorph.
// Persists to db.custom_npcs via POST /api/admin/npc/create.
export default function CreateNpcModal({
  open, onClose, requester, initial, onSaved,
}) {
  const isEdit = !!(initial && initial.name);
  const [name, setName] = React.useState('');
  const [npcType, setNpcType] = React.useState('NPC');
  const [gender, setGender] = React.useState('undisclosed');
  const [rooms, setRooms] = React.useState([]);
  const [color, setColor] = React.useState('#5a3a8a');
  const [description, setDescription] = React.useState('');
  const [customPrompt, setCustomPrompt] = React.useState('');
  const [hpMax, setHpMax] = React.useState('');
  const [isFemale, setIsFemale] = React.useState(false);
  // Msg #997 — Up to 5 picture URL slots. Slot 0 doubles as primary avatar.
  const EMPTY_PICS = React.useMemo(() => ['', '', '', '', ''], []);
  const [pictures, setPictures] = React.useState(EMPTY_PICS);
  const [busy, setBusy] = React.useState(false);
  const [genBusy, setGenBusy] = React.useState(false);  // Msg #999 — avatar gen
  const [err, setErr] = React.useState('');

  const generateAvatar = async (count = 1) => {
    if (!description.trim() || description.trim().length < 4) {
      setErr('Enter a Description first (≥ 4 chars) so the model has something to draw.');
      return;
    }
    setGenBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/npc/generate_avatar`, {
        requester,
        description: description.trim(),
        style: 'painterly character portrait',
        count,
      });
      const urls = Array.isArray(r.data?.image_urls) && r.data.image_urls.length
        ? r.data.image_urls
        : [r.data?.image_url].filter(Boolean);
      if (!urls.length) throw new Error('No image URLs in response.');
      setPictures((prev) => {
        const next = prev.slice();
        // Msg #1000 — Overwrite slots 0..(urls.length-1). Empty strings from
        // partial failures just leave the existing slot untouched.
        urls.forEach((u, i) => { if (u && i < next.length) next[i] = u; });
        return next;
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d || 'Avatar generation failed.');
    } finally {
      setGenBusy(false);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    setErr(''); setBusy(false);
    if (initial) {
      setName(initial.name || '');
      setNpcType((initial.npc_type || 'NPC').toUpperCase());
      setGender(initial.gender || 'undisclosed');
      setRooms(Array.isArray(initial.rooms) ? initial.rooms : []);
      setColor(initial.color || '#5a3a8a');
      setDescription(initial.description || '');
      setCustomPrompt(initial.custom_prompt || '');
      setHpMax(initial.hp_max ? String(initial.hp_max) : '');
      setIsFemale(!!initial.is_female);
      // Msg #997 — Seed picture slots from existing NPC record.
      const arr = Array.isArray(initial.pictures) && initial.pictures.length
        ? initial.pictures
        : (initial.picture ? [initial.picture] : []);
      setPictures(Array.from({ length: 5 }, (_, i) => arr[i] || ''));
    } else {
      setName(''); setNpcType('NPC'); setGender('undisclosed');
      setRooms([]); setColor('#5a3a8a'); setDescription('');
      setCustomPrompt(''); setHpMax(''); setIsFemale(false);
      setPictures(EMPTY_PICS);
    }
  }, [open, initial, EMPTY_PICS]);

  if (!open) return null;

  const toggleRoom = (r) => {
    setRooms((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };
  const submit = async () => {
    setErr('');
    if (!name.trim() || !/^[A-Za-z0-9_-]{2,40}$/.test(name.trim())) {
      setErr('Name must be 2-40 chars: letters, digits, _ or -'); return;
    }
    setBusy(true);
    try {
      const cleanedPics = pictures.map((p) => (p || '').trim());
      const payload = {
        requester,
        name: name.trim(),
        npc_type: npcType,
        gender,
        rooms,
        color,
        description: description.trim(),
        custom_prompt: customPrompt.trim() || undefined,
        hp_max: hpMax ? parseInt(hpMax, 10) : null,
        is_female: isFemale,
        // Msg #997 — Up to 5 image URLs.
        pictures: cleanedPics,
      };
      const r = await axios.post(`${API}/admin/npc/create`, payload);
      if (onSaved) onSaved(r.data && r.data.npc);
      onClose();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'NPC create failed.');
    } finally { setBusy(false); }
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1200,
    background: 'rgba(20,40,20,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const shell = {
    width: 'min(96vw, 620px)', maxHeight: '90vh', overflowY: 'auto',
    background: '#f2e8c8', border: '2px solid #7a5a1a',
    padding: '16px 20px', fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#3a2b14',
  };
  const row = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' };
  const label = { minWidth: 110, fontSize: 12, fontWeight: 'bold' };
  const input = { flex: 1, padding: '4px 6px', border: '1px solid #5a4a1d', fontFamily: 'inherit', fontSize: 12 };

  return (
    <div style={overlay} data-testid="create-npc-modal" onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 'bold', textTransform: 'uppercase',
                      letterSpacing: 2, marginBottom: 10 }}>
          🐣 {isEdit ? 'Edit' : 'Create'} Custom NPC
        </div>
        <div style={row}>
          <span style={label}>Name</span>
          <input
            data-testid="create-npc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sage_Idris"
            disabled={isEdit}
            style={{ ...input, background: isEdit ? '#e6dcb0' : '#fff' }}
            autoFocus
          />
        </div>
        <div style={row}>
          <span style={label}>Type</span>
          <select
            data-testid="create-npc-type"
            value={npcType}
            onChange={(e) => setNpcType(e.target.value)}
            style={input}
          >
            <option value="NPC">NPC (humanoid)</option>
            <option value="MONSTER">MONSTER</option>
            <option value="POKEMON">POKEMON</option>
            <option value="POKEMORPH">POKEMORPH</option>
            <option value="CHAMPION">CHAMPION</option>
          </select>
          <span style={{ ...label, minWidth: 60 }}>Gender</span>
          <select
            data-testid="create-npc-gender"
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              if (e.target.value === 'female') setIsFemale(true);
              if (e.target.value === 'male') setIsFemale(false);
            }}
            style={{ ...input, flex: '0 0 130px' }}
          >
            <option value="undisclosed">undisclosed</option>
            <option value="female">female</option>
            <option value="male">male</option>
            <option value="other">other</option>
          </select>
        </div>
        <div style={row}>
          <span style={label}>Color</span>
          <input
            data-testid="create-npc-color-hex"
            type="text" value={color}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9a-fA-F]{6}$/.test(v)) setColor(v.toLowerCase());
              else if (/^[0-9a-fA-F]{6}$/.test(v)) setColor('#' + v.toLowerCase());
              else setColor(v);
            }}
            style={{ ...input, flex: '0 0 120px', fontFamily: 'monospace' }}
            placeholder="#RRGGBB"
          />
          <label
            title="Open OS color wheel"
            style={{
              padding: '2px 8px', cursor: 'pointer',
              background: 'linear-gradient(45deg,#ff004d,#ffbe0b,#8ac926,#1982c4,#6a4c93,#ff004d)',
              color: '#fff', fontWeight: 'bold', fontSize: 11,
              textShadow: '0 0 2px rgba(0,0,0,0.9)', border: '1px solid #5a4a1d',
            }}
          >
            🎨
            <input
              data-testid="create-npc-color-wheel"
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#5a3a8a'}
              onChange={(e) => setColor(e.target.value.toLowerCase())}
              style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
            />
          </label>
          <span style={{ color, fontWeight: 'bold', padding: '0 6px' }}>Preview</span>
          <span style={{ ...label, minWidth: 60 }}>HP max</span>
          <input
            data-testid="create-npc-hp-max"
            type="number" value={hpMax}
            onChange={(e) => setHpMax(e.target.value)}
            placeholder="(auto)"
            style={{ ...input, flex: '0 0 90px' }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <input
              data-testid="create-npc-is-female"
              type="checkbox" checked={isFemale}
              onChange={(e) => setIsFemale(e.target.checked)}
            />
            Female (hearts affection)
          </label>
        </div>
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>Rooms</span>
          <div
            data-testid="create-npc-rooms"
            style={{
              flex: 1, maxHeight: 120, overflowY: 'auto',
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2,
              border: '1px solid #5a4a1d', padding: 4, background: '#fff',
              fontSize: 11,
            }}
          >
            {DEFAULT_ROOMS.map((r) => (
              <label
                key={r}
                data-testid={`create-npc-room-${r}`}
                style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={rooms.includes(r)}
                  onChange={() => toggleRoom(r)}
                />
                {r}
              </label>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 10, opacity: 0.75, marginTop: -4, marginBottom: 8, marginLeft: 118 }}>
          Leave empty to use type default (POKEMON→Pokemon rooms, MONSTER→Arena/Forest/…, NPC→2-room pool).
        </div>
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>Description</span>
          <textarea
            data-testid="create-npc-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Appearance, personality, tone, quirks. Feeds the auto-generated LLM prompt."
            style={{ ...input, resize: 'vertical', minHeight: 60 }}
          />
        </div>
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>Custom prompt<br /><small style={{ opacity: 0.7, fontWeight: 'normal' }}>(optional override)</small></span>
          <textarea
            data-testid="create-npc-custom-prompt"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            rows={3}
            placeholder="Full LLM system prompt override. Leave blank to auto-generate from description."
            style={{ ...input, resize: 'vertical', minHeight: 50 }}
          />
        </div>
        {/* Msg #997 — Up to 5 picture URL slots. Slot 0 doubles as main avatar. */}
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>
            Picture URLs
            <div style={{ fontSize: 10, fontWeight: 'normal', color: '#5a4a1d', marginTop: 2 }}>
              (up to 5, slot 1 = avatar)
            </div>
            {/* Msg #999 — Quick-generate NPC avatar via Gemini Nano Banana.
                Msg #1000 — Second button generates 5 distinct framings
                (portrait / full-body / action / profile / candid) and fills
                every slot in one click. */}
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button
                type="button"
                data-testid="create-npc-generate-avatar"
                onClick={() => generateAvatar(1)}
                disabled={genBusy || busy}
                title="Generate ONE AI portrait for slot 1 from the Description above"
                style={{
                  background: genBusy ? '#666' : 'linear-gradient(90deg,#ff004d,#ffbe0b,#8ac926,#1982c4,#6a4c93)',
                  color: '#fff', fontWeight: 'bold', fontSize: 10,
                  padding: '4px 8px', border: '1px solid #3a1a4a',
                  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                  cursor: (genBusy || busy) ? 'wait' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: 1,
                }}
              >
                {genBusy ? '🎨 Drawing…' : '✨ Auto (Slot 1)'}
              </button>
              <button
                type="button"
                data-testid="create-npc-generate-all-5"
                onClick={() => generateAvatar(5)}
                disabled={genBusy || busy}
                title="Generate 5 different framings (portrait / full-body / action / profile / candid) and fill every slot"
                style={{
                  background: genBusy ? '#666' : 'linear-gradient(90deg,#6a4c93,#1982c4,#8ac926,#ffbe0b,#ff004d)',
                  color: '#fff', fontWeight: 'bold', fontSize: 10,
                  padding: '4px 8px', border: '1px solid #3a1a4a',
                  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                  cursor: (genBusy || busy) ? 'wait' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: 1,
                }}
              >
                {genBusy ? '🎨 Drawing 5…' : '🎨 Fill All 5 Slots'}
              </button>
            </div>
          </span>
          <div style={{ flex: 1, minWidth: 0 }} data-testid="create-npc-pictures-list">
            {pictures.map((val, i) => (
              <div
                key={i}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}
              >
                <div
                  style={{
                    width: 34, height: 34, minWidth: 34,
                    background: '#e0d3a3', border: '1px solid #7a5a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {val ? (
                    <img
                      src={val}
                      alt={`slot ${i + 1}`}
                      style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  ) : (
                    <span style={{ fontSize: 16, opacity: 0.35 }}>◇</span>
                  )}
                </div>
                <input
                  data-testid={`create-npc-picture-url-${i}`}
                  value={val || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPictures((prev) => {
                      const next = prev.slice();
                      next[i] = v;
                      return next;
                    });
                  }}
                  placeholder={i === 0
                    ? 'https://…/avatar.png (main)'
                    : `https://…/slot-${i + 1}.png`}
                  style={{
                    flex: 1, padding: '3px 6px', border: '1px solid #5a4a1d',
                    fontFamily: 'inherit', fontSize: 11, minWidth: 0,
                  }}
                />
                {val && (
                  <button
                    type="button"
                    data-testid={`create-npc-picture-clear-${i}`}
                    onClick={() => setPictures((prev) => {
                      const next = prev.slice();
                      next[i] = '';
                      return next;
                    })}
                    style={{
                      backgroundColor: '#5d2a2a', color: '#fce2e2',
                      border: '1px solid #3a1a1a', fontSize: 10,
                      padding: '2px 6px', cursor: 'pointer',
                    }}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        </div>
        {err && (
          <div data-testid="create-npc-error" style={{ color: '#7a1f1f', fontSize: 12, marginBottom: 6 }}>
            {err}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ background: '#5a4a1d', color: '#f5e6c0',
                     border: '1px solid #3a2b14', padding: '4px 14px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >Cancel</button>
          <button
            data-testid="create-npc-submit"
            onClick={submit}
            disabled={busy}
            style={{ background: '#4a3a1a', color: '#ffe1a0',
                     border: '1px solid #a08a30', padding: '4px 14px',
                     fontWeight: 'bold', cursor: busy ? 'wait' : 'pointer',
                     opacity: busy ? 0.7 : 1 }}
          >{busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}
